import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../../hooks/useToast';
import { useAuth } from '../../auth/context';
import Switch from '../../../components/ui/Switch';
import ScrollMoreHint from '../../../components/ui/ScrollMoreHint';
import useViewportWidth from '../../../hooks/useViewportWidth';
import ShareChannels from '../../../shared/share/ShareChannels';
import { analytics } from '../../../shared/analytics';
import {
  ShareState,
  ShareVisibility,
  fetchShareSettings,
  updateShareSettings,
  composeShareUrl,
  buildShareMessage,
  sendShareEmail,
} from '../../share/api';

/**
 * Trip sharing v1 — the OWNER's share dialog (spec §7, trip-level).
 *
 * One dialog for the whole share lifecycle: pick who can open the trip
 * (Only me / Anyone with the link / Link + password), opt into sharing
 * booking details, and send the link through the shared ShareChannels block.
 * "Send by email" through Jody is offered to signed-in accounts; a guest's
 * Email tile opens their email app (ShareChannels).
 * Every mode change persists immediately (PUT /api/trips/:id/share) with a
 * success/error toast (ui-conventions: persisted changes get feedback).
 * Switching back to "Only me" revokes: the old link stops working, and
 * re-enabling mints a fresh one.
 *
 * Layout (Paulo 2026-10-03): on desktop widths two columns, who can open it
 * on the left, the link, message and channels on the right, so the channels
 * are visible without scrolling. Narrower screens stack them, with a visible
 * scrollbar and a ↓ hint while more content sits below (ScrollMoreHint).
 *
 * Opened from the trip detail AND journey detail kebabs (permission is always
 * on the whole trip, product decision 2026-09-10) and by the owner email's
 * "Manage sharing" link (?share=1). From a journey page (`journeyId`) the
 * link opens that journey rather than the trip page (Paulo 2026-10-03).
 */

export interface TripShareDialogProps {
  tripId: string;
  /** Shared from a journey page: the link lands on this journey. */
  journeyId?: string;
  tripTitle: string;
  source: 'trip_detail' | 'journey_detail' | 'my_trips';
  onClose: () => void;
  /** Notify the page so its share tag can update without a refetch. */
  onVisibilityChange?: (visibility: ShareVisibility) => void;
}

const MODE_OPTIONS: Array<{ value: ShareVisibility; label: string; description: string }> = [
  { value: 'private', label: 'Only me', description: 'The trip is visible only in your account.' },
  { value: 'link', label: 'Anyone with the link', description: 'People with the link can view the trip and all its routes, read-only.' },
  { value: 'password', label: 'Link + password', description: 'Like the link, plus a small password Jody generates for you.' },
];

/** Two-column layout from this viewport width (the dialog is then 860px). */
const WIDE_MIN_WIDTH = 880;

const TripShareDialog: React.FC<TripShareDialogProps> = ({ tripId, journeyId, tripTitle, source, onClose, onVisibilityChange }) => {
  const { showToast } = useToast();
  // Guests have no `user` (device token only): no Jody send for them.
  const { user } = useAuth();
  const [state, setState] = useState<ShareState | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const wide = useViewportWidth() >= WIDE_MIN_WIDTH;
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    analytics.trackTripShareDialogOpened({ trip_id: tripId, source, role: 'owner' });
    let alive = true;
    fetchShareSettings(tripId)
      .then((s) => { if (alive) setState(s); })
      .catch(() => { if (alive) setLoadError(true); });
    return () => { alive = false; };
  }, [tripId, source]);

  // Escape closes; scroll locks while open (shared dialog conventions).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const apply = useCallback(async (patch: { visibility?: ShareVisibility; includeBookings?: boolean }, successMessage: string) => {
    if (saving) return;
    setSaving(true);
    try {
      const next = await updateShareSettings(tripId, patch);
      setState(next);
      onVisibilityChange?.(next.visibility);
      showToast({ title: 'Sharing updated', message: successMessage, type: 'success', duration: 3000 });
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Could not update sharing. Please try again.', type: 'error' });
    } finally {
      setSaving(false);
    }
  }, [tripId, saving, showToast, onVisibilityChange]);

  const handleMode = (visibility: ShareVisibility) => {
    if (!state || state.visibility === visibility) return;
    const message =
      visibility === 'private' ? 'Sharing is off. The old link no longer works.'
        : visibility === 'password' ? 'Anyone with the link and the password can view this trip.'
          : 'Anyone with the link can view this trip.';
    apply({ visibility }, message);
  };

  const shareUrl = state ? composeShareUrl(state, journeyId) : null;
  const isShared = !!state && state.visibility !== 'private' && !!shareUrl;
  const shareMessage = isShared
    ? buildShareMessage({ tripTitle, url: shareUrl!, password: state!.password })
    : '';

  // The dialog's three parts, arranged by `wide` below.
  const modes = state && (
    <div role="radiogroup" aria-label="Who can open this trip" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
      {MODE_OPTIONS.map((opt) => {
        const active = state.visibility === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={saving}
            onClick={() => handleMode(opt.value)}
            style={{
              textAlign: 'left', display: 'flex', gap: 10, alignItems: 'flex-start',
              padding: '10px 12px', borderRadius: 12, cursor: saving ? 'default' : 'pointer',
              border: active ? '1.5px solid #157a55' : '1px solid rgba(20,40,30,0.12)',
              background: active ? '#e7f1ec' : '#fff', fontFamily: 'inherit',
            }}
          >
            <span
              aria-hidden
              style={{
                marginTop: 2, width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                border: active ? '5px solid #157a55' : '1.5px solid #b3bbb6', background: '#fff', boxSizing: 'border-box',
              }}
            />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#16241d' }}>{opt.label}</span>
              <span style={{ display: 'block', fontSize: 12.5, color: '#6c7872', lineHeight: 1.45 }}>{opt.description}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
  const bookingsToggle = state && isShared && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: '#f4f6f3', marginBottom: 14 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#16241d' }}>Include booking details</div>
        <div style={{ fontSize: 12, color: '#6c7872', lineHeight: 1.45 }}>
          Stays and tickets you saved (names, addresses, times, and the
          prices you entered) become visible. When off, viewers see
          Jody's estimates only.
        </div>
      </div>
      <Switch
        on={state.includeBookings}
        disabled={saving}
        ariaLabel="Include booking details for viewers"
        onToggle={() => apply(
          { includeBookings: !state.includeBookings },
          state.includeBookings ? 'Booking details are hidden from viewers.' : 'Booking details are visible to viewers.',
        )}
      />
    </div>
  );
  const channels = state && isShared && (
    <ShareChannels
      shareText={shareMessage}
      shareUrl={shareUrl!}
      password={state.visibility === 'password' ? state.password : null}
      emailSubject={`A trip on Jody: ${tripTitle}`}
      shareTitle="Jody"
      sendEmail={user
        ? (to, note) => sendShareEmail(tripId, { to, message: note, journeyId })
        : undefined}
      onChannel={(channel, detail) => analytics.trackTripShareChannelClicked({ trip_id: tripId, channel, role: 'owner', ...detail })}
      copyToastMessage="Paste it anywhere to share this trip."
      copyLinkToastMessage="Paste it anywhere to share this trip."
    />
  );

  const dialog = (
    <div
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: 'rgba(15, 26, 20, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        ref={scrollRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="trip-share-title"
        className="scroll-visible"
        style={{
          width: wide ? 'min(860px, 94vw)' : 'min(460px, 94vw)', maxHeight: '86vh', overflowY: 'auto',
          background: '#fff', borderRadius: 16, padding: '20px 20px 18px',
          boxShadow: '0 24px 56px rgba(15, 23, 42, 0.25)', position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: 12, right: 12, padding: '4px 8px', fontSize: 16,
            lineHeight: 1, background: 'transparent', border: 0, color: '#6c7872', cursor: 'pointer',
          }}
        >
          ×
        </button>

        <h3 id="trip-share-title" style={{ fontSize: 18, fontWeight: 700, color: '#16241d', margin: '0 0 2px' }}>
          Share this trip
        </h3>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6c7872', lineHeight: 1.5 }}>
          {journeyId
            ? <>"{tripTitle}": the link opens this route, and viewers can see every route, read-only. You can change this or turn it off at any time.</>
            : <>"{tripTitle}": viewers see every route, read-only. You can change this or turn it off at any time.</>}
        </p>

        {loadError ? (
          <div style={{ fontSize: 13.5, color: '#cf5a3c', padding: '8px 0' }}>
            Could not load the sharing settings. Close this and try again.
          </div>
        ) : !state ? (
          <div style={{ fontSize: 13.5, color: '#6c7872', padding: '8px 0' }}>Loading…</div>
        ) : wide ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 320px) minmax(0, 1fr)', gap: 24, alignItems: 'start' }}>
            <div>
              {modes}
              {bookingsToggle}
            </div>
            <div>
              {channels || (
                <div style={{ padding: '16px 14px', borderRadius: 12, background: '#f4f6f3', fontSize: 13, color: '#6c7872', lineHeight: 1.5 }}>
                  Only you can open this trip. Pick "Anyone with the link" or "Link + password" to get a link you can send.
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {modes}
            {bookingsToggle}
            {channels}
          </>
        )}
        <ScrollMoreHint scrollRef={scrollRef} />
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
};

export default TripShareDialog;
